import { AccountBalanceOutlined, CalendarMonthOutlined } from '@mui/icons-material';
import { Box, Theme, Typography, useTheme } from '@mui/material';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { AttendanceStatus, Event } from '../../../types/Event';
import { UserConfigurableModule } from '../../../types/User';
import { humanizeEventDateTimes } from '../../../utility/dateUtils';
import DrawerLink from '../../common/drawers/ModuleDrawer/DrawerLink';
import {
  secondaryIconStyles,
  secondaryLabelStyles,
  secondaryTextStyles,
  textContainerStyles,
  tileContainerStyles,
  tileHeaderLeftStyles,
  tileHeaderSectionStyles,
  tileSectionLeftStyles,
  tileSectionRightStyles,
} from '../dashboardStyles';
import { EventAttendance } from '../../../services/react-query/events';
import { AttendanceActions } from './AttendanceActions';

export interface IEventTileProps {
  event: Event;
  attendance?: EventAttendance;
}

// Returns the color for the attendance status based on the attendance status.
export const attendanceStatusColor = (status: AttendanceStatus, theme: Theme): string => {
  switch (status) {
    case AttendanceStatus.PENDING:
      return theme.palette.warning.main;
    case AttendanceStatus.ACCEPTED:
      return theme.palette.success.main;
    case AttendanceStatus.DECLINED:
      return theme.palette.error.main;
  }
};

export function EventTile({ event, attendance }: IEventTileProps) {
  const theme = useTheme();

  const attendanceStyles = useMemo(() => {
    if (!attendance?.status) return {};

    return {
      borderLeft: `5px solid ${attendanceStatusColor(attendance.status, theme)}`,
    };
  }, [attendance]);

  const dateLabel = humanizeEventDateTimes(event);
  return (
    <Box sx={[tileContainerStyles, attendanceStyles]}>
      <Box sx={tileSectionLeftStyles}>
        <Box sx={tileHeaderSectionStyles}>
          <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.uuid}>
            <Typography title={event.name} sx={tileHeaderLeftStyles}>
              {event.name}
            </Typography>
          </DrawerLink>
        </Box>

        <Box sx={textContainerStyles}>
          <Box sx={secondaryTextStyles}>
            <CalendarMonthOutlined sx={secondaryIconStyles} />
            <Typography
              title={dateLabel}
              sx={{
                ...secondaryLabelStyles,
                textDecoration: 'none',
              }}
            >
              {dateLabel}
            </Typography>
          </Box>

          <Box sx={secondaryTextStyles}>
            <AccountBalanceOutlined sx={secondaryIconStyles} />

            <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={event.account_uuid}>
              <Typography title={event.account_display_name} sx={secondaryLabelStyles}>
                {event.account_display_name}
              </Typography>
            </DrawerLink>
          </Box>
        </Box>
      </Box>
      <Box sx={tileSectionRightStyles}>
        <AttendanceActions event={event} attendance={attendance} />
      </Box>
    </Box>
  );
}
